import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { labelFor } from "@/lib/enumLabels";
import { useTranslation } from "react-i18next";
import {
  listClients, createClient, updateClient, deleteClient, clientCadence,
  viesPendingCount,
} from "../lib/api";
import { CadenceBadge } from "../components/CadenceBadge";
import { ClientEditForm } from "../components/ClientEditForm";
import { ThemeToggle } from "../components/ThemeToggle";

/**
 * v0.4.35 P6A — ADMIN client management.
 *
 * Two states: list view (table of clients with code/name/regs/
 * cadence) and edit view (form for one client). Create flow
 * pre-fills with empty defaults and switches to edit on POST.
 */
export default function AdminClients() {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null);
  const [cadences, setCadences] = useState({});
  const [viesPending, setViesPending] = useState({});  // v0.4.37 U1
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // v0.5.0h+1 — client-side substring filter on code/name.
  const [query, setQuery] = useState("");
  const nav = useNavigate();

  const reload = async () => {
    setLoading(true); setError(null);
    try {
      const rows = await listClients();
      setClients(rows);
      // v0.4.72 perf — parallelise per-client cadence + viesPending
      // fetches. Was sequential (2N round-trips for N clients); now
      // a single Promise.all wave so the new light list isn't stuck
      // on "Loading…" for many seconds with 5+ clients.
      const results = await Promise.all(rows.map(async (c) => {
        const [cad, pv] = await Promise.all([
          clientCadence(c.id).then((r) => r.deadlines).catch(() => []),
          viesPendingCount(c.id).catch(() => 0),
        ]);
        return [c.id, cad, pv];
      }));
      const cad = {};
      const pv = {};
      for (const [cid, deadlines, pending] of results) {
        cad[cid] = deadlines;
        pv[cid] = pending;
      }
      setCadences(cad);
      setViesPending(pv);
    } catch (e) { setError(e?.message || String(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  // v0.6.8 fix — Step6FreezeExport's IOSS/OSS readiness prereqs deep-link
  // here with `?edit=<client_id>` so an operator lands directly on the
  // right client's edit form instead of the bare list (they'd previously
  // been sent to the unrelated /settings page entirely — operator-
  // reported 2026-09-04). Runs once clients have loaded so the matching
  // row is available to open.
  //
  // v0.6.8 follow-up fix (found in sweep): this used to have no
  // "already consumed" guard, so it re-ran on EVERY `clients` array
  // change — including the reload after saving a completely different
  // client. The URL's `?edit=` param is never cleared, so any later
  // save/delete (of any client) silently snapped the operator's view
  // back onto the originally deep-linked client, discarding whatever
  // they had navigated to since. Now it fires at most once per page
  // load.
  const deepLinkConsumed = useRef(false);
  useEffect(() => {
    if (loading || clients.length === 0 || deepLinkConsumed.current) return;
    const editId = new URLSearchParams(window.location.search).get("edit");
    if (!editId) return;
    deepLinkConsumed.current = true;
    const match = clients.find((c) => c.id === editId);
    if (match) setEditing(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, clients]);

  const onSave = async (form, isNew) => {
    try {
      if (isNew) await createClient(form);
      else await updateClient(editing.id, form);
      setEditing(null); await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // FastAPI 422 returns an array of validation errors; flatten to
      // human-readable lines. Custom 400/422 from our routes use the
      // `{error, message}` dict shape — render the message.
      let msg = e.message;
      if (Array.isArray(detail)) {
        msg = detail.map((d) =>
          `${(d.loc || []).slice(-1)[0] || "?"}: ${d.msg || JSON.stringify(d)}`
        ).join(" · ");
      } else if (detail && typeof detail === "object") {
        msg = detail.message || JSON.stringify(detail);
      } else if (typeof detail === "string") {
        msg = detail;
      }
      throw new Error(msg);
    }
  };

  const onDelete = async (c) => {
    const ok = window.confirm(
      t("ui.admin_clients.delete_confirm", { code: c.code, name: c.name }),
    );
    if (!ok) return;
    try { await deleteClient(c.id); await reload(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      alert(d?.message || d || e.message);
    }
  };

  if (editing) {
    return (
      <ClientEditForm
        initial={editing.isNew ? null : editing}
        onCancel={() => setEditing(null)}
        onSave={onSave}
      />
    );
  }

  return (
    <div data-testid="admin-clients-page" className="min-h-screen bg-slate-50 dark:bg-slate-950 px-6 py-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50">{t("ui.admin_clients.title")}</h1>
          <div className="flex gap-2">
            <ThemeToggle />
            <button
              data-testid="admin-clients-back"
              onClick={() => nav("/")}
              className="text-sm text-slate-700 dark:text-slate-200 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 dark:bg-slate-800 rounded-lg font-medium"
            >{t("ui.admin_clients.back_to_dashboard")}</button>
            <button
              data-testid="admin-clients-new"
              onClick={() => setEditing({ isNew: true })}
              className="bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm"
            >{t("ui.admin_clients.new_client")}</button>
          </div>
        </div>
        {loading && <div className="text-slate-600 dark:text-slate-300 text-sm py-4">{t("ui.admin_clients.loading")}</div>}
        {error && <div className="text-rose-700 dark:text-rose-300 text-sm bg-rose-50 dark:bg-rose-900/40 border border-rose-200 dark:border-rose-800 px-4 py-3 rounded-lg">{error}</div>}
        {!loading && !error && (() => {
          const q = query.trim().toLowerCase();
          const visible = q
            ? clients.filter((c) =>
                (c.code || "").toLowerCase().includes(q)
                || (c.name || "").toLowerCase().includes(q),
              )
            : clients;
          return (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  type="search"
                  data-testid="admin-clients-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("ui.admin_clients.search_placeholder")}
                  className="flex-1 min-w-[280px] bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-50 placeholder:text-slate-400 dark:placeholder:text-slate-500 px-3 py-2 text-sm rounded-lg focus:outline-none focus:border-teal-500 dark:focus:border-teal-400"
                />
                <span
                  data-testid="admin-clients-search-count"
                  className="tabular-nums text-[11px] text-slate-500 dark:text-slate-400 shrink-0"
                >
                  {q
                    ? t("ui.admin_clients.search_match", { visible: visible.length, total: clients.length })
                    : t(clients.length === 1 ? "ui.admin_clients.count_single" : "ui.admin_clients.count_plural", { total: clients.length })}
                </span>
              </div>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900 shadow-sm">
                <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 text-xs font-medium text-slate-600 dark:text-slate-300">
                  <div className="col-span-1">{t("ui.admin_clients.col_code")}</div>
                  <div className="col-span-3">{t("ui.admin_clients.col_name")}</div>
                  <div className="col-span-1">{t("ui.admin_clients.col_established")}</div>
                  <div className="col-span-1 text-right">{t("ui.admin_clients.col_vat_regs")}</div>
                  <div className="col-span-2">{t("ui.admin_clients.col_filing_scheme")}</div>
                  <div className="col-span-3">{t("ui.admin_clients.col_next_deadline")}</div>
                  <div className="col-span-1 text-right">{t("ui.admin_clients.col_actions")}</div>
                </div>
                {visible.map((c) => (
                  <div
                    key={c.id}
                    data-testid={`client-row-${c.id}`}
                    className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-slate-100 dark:border-slate-800 text-sm items-center hover:bg-slate-50 dark:hover:bg-slate-800/60 dark:bg-slate-950 transition-colors"
                  >
                    <div className="col-span-1 tabular-nums text-amber-700 dark:text-amber-300 font-semibold">{c.code}</div>
                    <div className="col-span-3 text-slate-900 dark:text-slate-50 font-medium">{c.name}</div>
                    <div className="col-span-1 text-slate-600 dark:text-slate-300 text-xs">{labelFor("seller_establishment", c.seller_establishment)}</div>
                    <div className="col-span-1 text-right tabular-nums text-slate-700 dark:text-slate-200">{(c.company_vat_numbers || []).length}</div>
                    <div className="col-span-2 text-slate-600 dark:text-slate-300 text-xs">{labelFor("filing_model", c.filing_model || c.derived_filing_model)}</div>
                    <div className="col-span-3"><CadenceBadge deadlines={cadences[c.id] || []} pendingVies={viesPending[c.id] || 0} /></div>
                    <div className="col-span-1 flex gap-3 justify-end items-center">
                      <button
                        data-testid={`client-edit-${c.id}`}
                        onClick={() => setEditing(c)}
                        className="text-teal-700 dark:text-teal-300 hover:text-teal-800 dark:hover:text-teal-200 text-xs font-medium hover:underline"
                      >{t("ui.admin_clients.action_edit")}</button>
                      <button
                        data-testid={`client-history-${c.id}`}
                        onClick={() => nav(`/clients/${c.id}/history`)}
                        className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-50 text-xs font-medium hover:underline"
                      >{t("ui.admin_clients.action_history")}</button>
                      {!c.is_default && (
                        <button
                          data-testid={`client-delete-${c.id}`}
                          onClick={() => onDelete(c)}
                          title={t("ui.admin_clients.delete_tooltip")}
                          className="text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 dark:text-rose-300 text-lg leading-none px-1"
                        >×</button>
                      )}
                    </div>
                  </div>
                ))}
                {clients.length === 0 && (
                  <div className="px-5 py-10 text-sm text-slate-500 dark:text-slate-400 text-center italic">
                    {t("ui.admin_clients.empty")}
                  </div>
                )}
                {clients.length > 0 && visible.length === 0 && (
                  <div
                    data-testid="admin-clients-no-match"
                    className="px-5 py-10 text-sm text-slate-500 dark:text-slate-400 text-center"
                  >
                    {t("ui.admin_clients.no_match", { q: query })}
                  </div>
                )}
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
}
